import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components/ui/card';
import { Button } from '@/ui/components/ui/button';
import { Input } from '@/ui/components/ui/input';
import { Label } from '@/ui/components/ui/label';
import { useAppContext } from '@/store/AppContext';
import { Percent, Plus, Trash2 } from 'lucide-react';

export function Discounts() {
  const { discounts, addDiscount, removeDiscount } = useAppContext();
  const [newWholesaler, setNewWholesaler] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newPercentage, setNewPercentage] = useState('');

  const handleAddDiscount = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWholesaler || !newCategory || !newPercentage) return;
    
    addDiscount({
      wholesaler: newWholesaler,
      category: newCategory,
      percentage: parseFloat(newPercentage),
    });
    
    setNewWholesaler('');
    setNewCategory('');
    setNewPercentage('');
  };

  return (
    <div className="p-8 space-y-8 max-w-4xl">
      <div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <Percent className="w-8 h-8 text-emerald-500" />
          Mój Portfel Rabatowy
        </h2>
        <p className="text-slate-500 mt-2">
          Dodaj swoje zniżki u dostawców. System automatycznie uwzględni je w wycenach, 
          a różnica między ceną rynkową a Twoją ceną zakupu trafi do Twojej kieszeni!
        </p>
      </div>

      <Card className="bg-slate-50 border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">Dodaj nowy rabat</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAddDiscount} className="flex flex-col md:flex-row gap-4 items-end">
            <div className="space-y-2 flex-1">
              <Label htmlFor="wholesaler">Hurtownia / Sklep</Label>
              <Input 
                id="wholesaler" 
                placeholder="np. PSB, Castorama, Hurtownia X" 
                value={newWholesaler}
                onChange={(e) => setNewWholesaler(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2 flex-1">
              <Label htmlFor="category">Kategoria materiałów</Label>
              <Input 
                id="category" 
                placeholder="np. Chemia budowlana, Płyty GK" 
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2 w-32">
              <Label htmlFor="percentage">Rabat (%)</Label>
              <Input 
                id="percentage" 
                type="number" 
                min="0" 
                max="100" 
                placeholder="np. 15" 
                value={newPercentage}
                onChange={(e) => setNewPercentage(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
              <Plus className="w-4 h-4" /> Dodaj
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-slate-900">Twoje aktywne rabaty</h3>
        {discounts.length === 0 ? (
          <Card className="p-8 text-center text-slate-500">
            Brak dodanych rabatów. Dodaj swój pierwszy rabat powyżej.
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {discounts.map((discount) => (
              <Card key={discount.id} className="flex items-center justify-between p-4 hover:border-emerald-200 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-lg">
                    -{discount.percentage}%
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{discount.wholesaler}</h4>
                    <p className="text-sm text-slate-500">{discount.category}</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-slate-400 hover:text-red-500 hover:bg-red-50"
                  onClick={() => removeDiscount(discount.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
